import { Flex, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utilities";
import { CubePlay } from "@/components/icon/CubePlay";
import { CubePlayCancel } from "@/components/icon/CubePlayCancel";
import { CubePlayFinish } from "@/components/icon/CubePlayFinish";

const { Text } = Typography;

interface StatusSummaryItem {
  status: string;
  count: number;
  total: number;
}

interface StatusSummaryProps {
  orders: any;
}

const ALL_STATUSES = [
  "pending_payment",
  "review",
  "verified",
  "invoiced",
  "processing",
  "preparing",
  "ready_to_deliver",
  "in_shipping",
  "delivered",
  "cancel_request",
  "canceled",
];

export const StatusOrdersIcons: React.FC<StatusSummaryProps> = ({ orders }) => {
  const { t } = useTranslation();


  const getStatusSummary = (): StatusSummaryItem[] => {
    const num = (v: any, d = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : d;
    };

    const norm = (v: any) => String(v ?? "").toLowerCase().trim();

    const isCanceledStatus = (s: any) => {
      const x = norm(s);
      return x === "canceled" || x === "cancelled";
    };

    const isDeliveredStatus = (s: any) => norm(s) === "delivered";

    const isPendingShipment = (s: any) => {
      // “pendiente de entrega” = cualquier envío que NO esté delivered ni canceled
      return !isDeliveredStatus(s) && !isCanceledStatus(s);
    };

    const calcBucket = (order: any): "open" | "complete" | "canceled" => {
      const grandTotal = num(order?.grandTotal, 0);
      const totalPaid = num(order?.totalPaid, 0);

      // saldo pendiente: si no hay totalPaid -> pendiente
      const balance = grandTotal - totalPaid;
      const hasPendingPayment = balance > 0.01;

      const shipments = Array.isArray(order?.shipments) ? order.shipments : [];
      const payments = Array.isArray(order?.payments) ? order.payments : [];

      const hasPendingDelivery = shipments.some((sh: any) =>
        isPendingShipment(sh?.status),
      );

      // Cancelado: TODAS las transacciones de facturación y TODAS las entregas canceladas
      const hasAnyBillingOrDelivery = payments.length > 0 || shipments.length > 0;

      const allPaymentsCanceled =
        payments.length > 0 && payments.every((p: any) => isCanceledStatus(p?.status));

      const allShipmentsCanceled =
        shipments.length > 0 &&
        shipments.every((s: any) => isCanceledStatus(s?.status));

      const isFullyCanceled =
        hasAnyBillingOrDelivery && allPaymentsCanceled && allShipmentsCanceled;

      if (isFullyCanceled) return "canceled";
      if (!hasPendingPayment && !hasPendingDelivery) return "complete";
      return "open";
    };

    const summary: Record<"open" | "complete" | "canceled", { count: number; total: number }> = {
      open: { count: 0, total: 0 },
      complete: { count: 0, total: 0 },
      canceled: { count: 0, total: 0 },
    };

    (orders ?? []).forEach((order: any) => {
      const bucket = calcBucket(order);
      summary[bucket].count += 1;
      summary[bucket].total += num(order?.grandTotal, 0);
    });

    return [
      { status: "open", count: summary.open.count, total: summary.open.total },
      { status: "complete", count: summary.complete.count, total: summary.complete.total },
      { status: "canceled", count: summary.canceled.count, total: summary.canceled.total },
    ];
  };


  const statusSummary = getStatusSummary();

  const get = (key: "open" | "complete" | "canceled") =>
    statusSummary.find((x) => x.status === key) ?? { count: 0, total: 0 };

  return (
    <>
      <Flex
        vertical
        justify="space-around"
        style={{ width: "100%", height: "100%" }}
      >
        <Flex justify="space-between" align="center" className="card-cube">
          <CubePlay />
          <Flex vertical justify="center" align="end">
            <Text strong style={{ color: "#2B8058" }}>
              {t("quotes.status.Open")}
            </Text>
            <Text>
              {get("open").count} x <strong>{formatPrice(get("open").total)}</strong>
            </Text>
          </Flex>
        </Flex>
        <Flex justify="space-between" align="center" className="card-cube">
          <CubePlayFinish />
          <Flex vertical justify="center" align="end">
            <Text strong style={{ color: "#2B8058" }}>
              {t("orders.status.complete")}
            </Text>
            <Text>
              {get("complete").count} x <strong>{formatPrice(get("complete").total)}</strong>
            </Text>
          </Flex>
        </Flex>
        <Flex justify="space-between" align="center" className="card-cube">
          <CubePlayCancel />
          <Flex vertical justify="center" align="end">
            <Text strong style={{ color: "#2B8058" }}>
              {t("orders.status.cancel")}
            </Text>
            <Text>
              {get("canceled").count} x <strong>{formatPrice(get("canceled").total)}</strong>
            </Text>
          </Flex>
        </Flex>

      </Flex>
    </>
  );
};
